// src/components/SpeedAnalyticsChart.tsx - COMPLETE CODE WITH MODAL

import React, { useState } from 'react';
import {
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    ToggleButtonGroup,
    ToggleButton,
    Chip
} from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Tooltip,
    CartesianGrid,
    Cell
} from 'recharts';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedDetailModal from './SpeedDetailModal';

interface Props {
    data: any;
}

type ViewMode = 'average' | 'max' | 'violations';

// ✅ Custom Tooltip with vehicle breakdown
const CustomSpeedTooltip = ({ active, payload, label, viewMode }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const MAX_VEHICLES_SHOWN = 5;

    // Sort vehicles based on view mode
    const sortedVehicles = data.vehicles
        ? [...data.vehicles].sort((a: any, b: any) => {
            if (viewMode === 'violations') return b.violations - a.violations;
            if (viewMode === 'max') return b.maxSpeed - a.maxSpeed;
            return b.avgSpeed - a.avgSpeed;
        })
        : [];

    const topVehicles = sortedVehicles.slice(0, MAX_VEHICLES_SHOWN);
    const remainingCount = sortedVehicles.length - MAX_VEHICLES_SHOWN;

    return (
        <Box
            sx={{
                bgcolor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 1.5,
                boxShadow: 2,
                minWidth: 240,
                maxWidth: 350,
                maxHeight: 400,
                overflow: 'auto',
            }}
        >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                {label}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Avg Speed
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="600">
                        {data.avgSpeed || data.value} km/h
                    </Typography>
                </Box>
                {data.maxSpeed && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Max Speed
                        </Typography>
                        <Typography variant="body2" color="error" fontWeight="600">
                            {data.maxSpeed} km/h
                        </Typography>
                    </Box>
                )}
                {data.violations > 0 && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Violations
                        </Typography>
                        <Typography variant="body2" color="warning.main" fontWeight="600">
                            {data.violations}
                        </Typography>
                    </Box>
                )}
            </Box>

            {topVehicles.length > 0 && (
                <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 1, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        {sortedVehicles.length > MAX_VEHICLES_SHOWN
                            ? `Top ${MAX_VEHICLES_SHOWN} ${viewMode === 'violations' ? 'Violators' : 'Contributors'}:`
                            : 'Vehicle Breakdown:'}
                    </Typography>
                    {topVehicles.map((vehicle: any, idx: number) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                py: 0.5,
                                gap: 1,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 600,
                                        display: 'block',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {vehicle.plateNumber || vehicle.imei}
                                </Typography>
                                {(vehicle.make || vehicle.model) && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: '0.65rem',
                                            display: 'block',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {vehicle.make} {vehicle.model}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography
                                    variant="caption"
                                    color="primary"
                                    sx={{
                                        fontWeight: 600,
                                        display: 'block',
                                    }}
                                >
                                    {viewMode === 'max' ? vehicle.maxSpeed : vehicle.avgSpeed} km/h
                                </Typography>
                                {viewMode !== 'violations' && vehicle.violations > 0 && (
                                    <Typography
                                        variant="caption"
                                        color="warning.main"
                                        sx={{
                                            fontSize: '0.65rem',
                                            display: 'block',
                                        }}
                                    >
                                        {vehicle.violations} violations
                                    </Typography>
                                )}
                                {viewMode === 'violations' && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{
                                            fontSize: '0.65rem',
                                            display: 'block',
                                        }}
                                    >
                                        {vehicle.violations} times
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}

                    {remainingCount > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 0.5,
                                mt: 0.5,
                                borderTop: '1px dashed #e0e0e0',
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                + {remainingCount} more vehicle{remainingCount > 1 ? 's' : ''}
                            </Typography>
                            <Typography variant="caption" color="primary" fontWeight="600">
                                Click for details
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {data.vehicleCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                    {data.vehicleCount} vehicle{data.vehicleCount > 1 ? 's' : ''} active
                </Typography>
            )}
        </Box>
    );
};

const SpeedAnalyticsChart: React.FC<Props> = ({ data }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [viewMode, setViewMode] = useState<ViewMode>('average');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDayData, setSelectedDayData] = useState<any>(null);

    console.log('🚗💨 Speed Chart Data:', {
        hasData: !!data,
        hasByDate: !!data?.byDate,
        length: data?.byDate?.length,
        summary: data?.summary
    });

    const speedData = data?.byDate || [];

    if (!speedData || speedData.length === 0) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Typography color="textSecondary" variant="body2" textAlign="center">
                    No speed data available
                </Typography>
            </Box>
        );
    }

    // Prepare chart data based on view mode
    const chartData = speedData
        .map((item: any) => ({
            date: new Date(item.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            }),
            fullDate: item.date,
            avgSpeed: item.avgSpeed || 0,
            maxSpeed: item.maxSpeed || 0,
            violations: item.violations || 0,
            value: viewMode === 'average' ? item.avgSpeed :
                viewMode === 'max' ? item.maxSpeed :
                    item.violations,
            vehicles: item.vehicles || [],
            vehicleCount: item.vehicleCount || 0,
        }))
        .slice(-7);

    const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: ViewMode | null) => {
        if (newView !== null) {
            setViewMode(newView);
        }
    };

    // ✅ Handle chart click to open modal
    const handleChartClick = (chartData: any) => {
        if (chartData && chartData.activePayload && chartData.activePayload[0]) {
            const dayData = chartData.activePayload[0].payload;
            setSelectedDayData({
                date: dayData.fullDate,
                avgSpeed: dayData.avgSpeed,
                maxSpeed: dayData.maxSpeed,
                violations: dayData.violations,
                vehicleCount: dayData.vehicleCount,
                vehicles: dayData.vehicles,
            });
            setModalOpen(true);
        }
    };

    return (
        <Box sx={{ width: '100%', height: '100%', minHeight: 250 }}>
            {/* View Mode Selector */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewChange}
                    size="small"
                    sx={{ flexWrap: 'wrap' }}
                >
                    <ToggleButton value="average" sx={{ px: isMobile ? 1 : 2 }}>
                        <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {!isMobile && 'Avg Speed'}
                    </ToggleButton>
                    <ToggleButton value="max" sx={{ px: isMobile ? 1 : 2 }}>
                        <SpeedIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {!isMobile && 'Max Speed'}
                    </ToggleButton>
                    <ToggleButton value="violations" sx={{ px: isMobile ? 1 : 2 }}>
                        <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {!isMobile && 'Violations'}
                    </ToggleButton>
                </ToggleButtonGroup>

                {data?.summary && (
                    <Chip
                        label={`Total Violations: ${data.summary.totalViolations}`}
                        color="warning"
                        size="small"
                    />
                )}
            </Box>

            {/* Chart */}
            <ResponsiveContainer width="100%" height="90%">
                {viewMode === 'violations' ? (
                    // Bar chart for violations
                    <BarChart
                        data={chartData}
                        onClick={handleChartClick}
                        margin={{
                            top: 5,
                            right: isMobile ? 10 : 30,
                            left: isMobile ? 0 : 20,
                            bottom: isMobile ? 20 : 5
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: isMobile ? 9 : 12 }}
                            angle={isMobile ? -45 : 0}
                            textAnchor={isMobile ? 'end' : 'middle'}
                            height={isMobile ? 60 : 30}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: isMobile ? 10 : 12 }}
                            width={isMobile ? 35 : 50}
                        />
                        <Tooltip content={(props) => <CustomSpeedTooltip {...props} viewMode={viewMode} />} />
                        <Bar
                            dataKey="violations"
                            radius={[8, 8, 0, 0]}
                            cursor="pointer"
                        >
                            {chartData.map((entry: any, index: number) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.violations > 10 ? '#f44336' : entry.violations > 5 ? '#ff9800' : '#ffc107'}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                ) : (
                    // Line chart for speed
                    <LineChart
                        data={chartData}
                        onClick={handleChartClick}
                        margin={{
                            top: 5,
                            right: isMobile ? 10 : 30,
                            left: isMobile ? 0 : 20,
                            bottom: isMobile ? 20 : 5
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: isMobile ? 9 : 12 }}
                            angle={isMobile ? -45 : 0}
                            textAnchor={isMobile ? 'end' : 'middle'}
                            height={isMobile ? 60 : 30}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: isMobile ? 10 : 12 }}
                            width={isMobile ? 40 : 60}
                            domain={[0, 'auto']}
                        />
                        <Tooltip content={(props) => <CustomSpeedTooltip {...props} viewMode={viewMode} />} />
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={viewMode === 'max' ? '#f44336' : '#2196f3'}
                            strokeWidth={isMobile ? 2 : 3}
                            dot={{
                                r: isMobile ? 3 : 4,
                                fill: viewMode === 'max' ? '#f44336' : '#2196f3',
                                cursor: 'pointer'
                            }}
                            name={viewMode === 'max' ? 'Max Speed' : 'Avg Speed'}
                            cursor="pointer"
                        />
                    </LineChart>
                )}
            </ResponsiveContainer>

            {/* ✅ Speed Detail Modal */}
            <SpeedDetailModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                date={selectedDayData?.date || ''}
                data={selectedDayData}
                viewMode={viewMode}
            />
        </Box>
    );
};

export default SpeedAnalyticsChart;